/* eslint-disable */
import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AsyncSelect from "react-select/async";
import { MaterialReactTable } from "material-react-table";
import { Box, IconButton, TextField, InputAdornment } from "@mui/material";
import SaveIcon from "@mui/icons-material/Check";
import CancelIcon from "@mui/icons-material/Close";
import { getData, putData } from "utils/Gateway";
import * as url from "helper/UrlHelper";
import TabsHeader from "components/Common/TabsHeader/TabsHeader";
import { commonHeaderLinkLists } from "helper/PatientManagementHelper/PatientManagementHelper";
import AccessDeniedView from "components/Common/AccessDeniedView/AccessDeniedView";
import AlertNotification from "components/Common/AlertNotification/AlertNotification";
import {
  getDateFormatInSpanish,
  mergeListById,
} from "helper/Common/CommonHelper";
import LoadingSkeleton from "components/Common/ListLoadingSkeleton/LoadingSkeleton";
import { assetImages } from "constants";
import DeleteWarningModal from "components/Common/Modal/DeleteWarningModal";
import AssignUserModal from "components/Common/Modal/AssignUserModal";
import ResultsHeader from "./Header/ResultsHeader";
import SaveResultPopup from "./Popup/SaveResultPopup";

const ResultsListBody = () => {
  const { t } = useTranslation();
  const token = localStorage.getItem("token");
  const moduleAccess = localStorage.getItem("moduleaccess");
  const userInfo = JSON.parse(localStorage.getItem("userInfo"));

  const [isLoading, setIsLoading] = useState(false);
  const [resultsList, setResultsList] = useState([]);
  const [rowSelection, setRowSelection] = useState({});
  const [selectedResultIds, setSelectedResultIds] = useState([]);
  const [selectedResultId, setSelectedResultId] = useState(null);

  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [messageType, setMessageType] = useState("");

  // get all results
  const getAllResults = async () => {
    try {
      setIsLoading(true);
      let requestUrl =
        url.API_BASE_URL + url.API_GET_ALL_MEDICAL_TEST;
      const response = await getData(requestUrl);
      setIsLoading(false);
      if (response.status) {
        setResultsList(response.data);
      }
    } catch (error) {
      setIsLoading(false);
    }
  };

  // bulk update results
  const updateBulkResultsHandler = async () => {
    if (selectedResultIds.length > 0) {
      try {
        const resultsListToBeUpdate = resultsList.filter((item) =>
          selectedResultIds.includes(item._id)
        );

        let resultsData = {
          medicaltestlist: resultsListToBeUpdate,
        };

        let requestURL =
          url.API_BASE_URL +
          url.API_UPDATE_BULK_MEDICAL_TEST +
          ``;

        const response = await putData(requestURL, resultsData);

        console.log(response);

        if (response.status) {
          setMessageType("success");

          setTimeout(() => {
            setSelectedResultIds([]);
            setRowSelection({});
          }, 1500);
        } else {
          setMessageType("error");
        }

        setAlertMessage(response.message);
      } catch (error) {
        setAlertMessage(error.message);
        setMessageType("error");
      }
    } else {
      setAlertMessage("Please select at least one result");
      setMessageType("error");
    }
    setShowAlert(true);
  };

  // bulk delete results
  const deleteBulkResultsHandler = async () => {
    if (selectedResultIds.length > 0) {
      try {
        const resultsListToBeDelete = resultsList.filter((item) =>
          selectedResultIds.includes(item._id)
        );

        let resultsData = {
          medicaltestlist: resultsListToBeDelete,
        };

        let requestURL =
          url.API_BASE_URL +
          url.API_DELETE_BULK_MEDICAL_TEST +
          ``;

        const response = await putData(requestURL, resultsData);

        console.log(response);

        if (response.status) {
          setMessageType("success");

          setTimeout(() => {
            setResultsList((prevList) =>
              prevList.filter((item) => !selectedResultIds.includes(item._id))
            );
          }, 1500);
        } else {
          setMessageType("error");
        }

        setAlertMessage(response.message);
      } catch (error) {
        setAlertMessage(error.message);
        setMessageType("error");
      }
    } else {
      setAlertMessage("Please select at least one result");
      setMessageType("error");
    }
    setShowAlert(true);
  };

  // After any action
  const afterActionClose = (data = []) => {
    setResultsList((prevList) => mergeListById(prevList, data));
  };

  useEffect(() => {
    getAllResults();
  }, []);

  const columns = useMemo(
    () => [
      {
        accessorKey: "#",
        header: "Actions",
        Cell: ({ row }) => (
          <button
            className="action_btn_mui"
            data-bs-toggle="offcanvas"
            data-bs-target="#saveResultOffCanvas"
            aria-controls="saveResultOffCanvas"
            onClick={() => {
              setSelectedResultId(row.original._id);
            }}
          >
            <span className="d-block material-symbols-outlined horz_icon">
              more_horiz
            </span>
          </button>
        ),
        enableColumnActions: false, // Hides the column action icon
        enableColumnDragging: false, // Hides the move icon
        enableSorting: false,
      },
      {
        header: "Test Name",
        accessorKey: "testname",
      },
      {
        header: "Test Category",
        accessorKey: "testcategory",
      },
      {
        header: "Order Date",
        accessorKey: "orderdate",
        Cell: ({ cell }) => cell.getValue().split("T")[0],
      },
      {
        header: "Test Date",
        accessorKey: "testdate",
        Cell: ({ cell }) => cell.getValue().split("T")[0],
      },
      {
        header: "Result Date",
        accessorKey: "resultdate",
        Cell: ({ cell }) => cell.getValue().split("T")[0],
      },
      {
        header: "Result",
        accessorKey: "result",
      },
      {
        header: "Unit",
        accessorKey: "unit",
      },
      {
        header: "Reference Range",
        accessorKey: "referencerange",
      },
      {
        header: "Severity",
        accessorKey: "severity",
      },
      {
        header: "Referred By",
        accessorKey: "referredby",
      },
      {
        header: "Provider",
        accessorKey: "provider",
      },
      {
        header: "Facility",
        accessorKey: "facility",
      },
      {
        header: "Assigned To",
        accessorKey: "assignedpersonname",
        Cell: ({ row }) => (
          <div
            className={
              row.original.assignedpersonname === ""
                ? "d-none"
                : "owner border-bottom-0 d-flex align-items-center gap-1"
            }
          >
            <img
              className="rounded-circle"
              height={35}
              width={35}
              src={
                row.original.assignedpersonimage == ""
                  ? assetImages.defaultUser
                  : url.SERVER_URL + row.original.assignedpersonimage
              }
              alt="User"
            />
            <p>
              <span className="d-block">{row.original.assignedpersonname}</span>
            </p>
          </div>
        ),
      },
    ],
    []
  );

  const columnOrder = [
    "#",
    "mrt-row-select",
    ...columns.map((c) => c.accessorKey),
  ];

  useEffect(() => {
    const selectedIdsArray = Object.keys(rowSelection).filter(
      (key) => rowSelection[key]
    );
    if (selectedIdsArray.length > 0) {
      setSelectedResultIds(selectedIdsArray);
    } else {
      setSelectedResultIds([]);
    }
  }, [rowSelection]);

  const onAlertClose = () => {
    setShowAlert(false);
    setAlertMessage("");
    setMessageType("");
  };

  if (
    userInfo.role.slug === "ADMIN" ||
    userInfo.role.slug === "SUPER_ADMIN" ||
    moduleAccess.includes("MOD_APPOINTMENT_MANAGEMENT")
  ) {
    return (
      <div id="content_wrapper">
        <section className="crm-wrapper bg-white pb-5">
          <TabsHeader
            commonHeaderObject={commonHeaderLinkLists(t)}
            activeOption={t("Results")}
          />
          <div className="container-fluid px-lg-5">
            <ResultsHeader
              selectedItemIds={selectedResultIds}
              updateBulkHandler={updateBulkResultsHandler}
              reloadList={getAllResults}
            />

            {isLoading ? (
              <LoadingSkeleton />
            ) : (
              <div className="table-wrapper">
                <MaterialReactTable
                  columns={columns}
                  data={resultsList}
                  enableGrouping // to enable grouping of column
                  enableRowSelection // enable showing checkbox in each row
                  getRowId={(row) => row._id}
                  onRowSelectionChange={setRowSelection}
                  state={{ rowSelection, columnOrder }}
                  initialState={{ rowSelection, columnOrder }} // to set the initial state of column order
                  defaultColumn={{
                    minSize: 20,
                    maxSize: 300,
                    size: 50, //make columns wider by default
                  }}
                  muiTableContainerProps={{
                    sx: {
                      maxHeight: "60vh",
                    },
                  }}
                  enableStickyHeader
                />
              </div>
            )}
          </div>

          {/* popup and modals */}
          <SaveResultPopup
            selectedResultId={selectedResultId}
            setSelectedResultId={setSelectedResultId}
            afterActionClose={afterActionClose}
            setShowAlert={setShowAlert}
            setAlertMessage={setAlertMessage}
            setMessageType={setMessageType}
          />

          <AssignUserModal
            moduleName="medicaltest"
            afterModalClose={afterActionClose}
            selectedIds={selectedResultIds}
            setShowAlert={setShowAlert}
            setAlertMessage={setAlertMessage}
            setMessageType={setMessageType}
            onModalClose={() => {
              setRowSelection({});
              setSelectedResultIds([]);
            }}
          />

          <DeleteWarningModal
            onDelete={deleteBulkResultsHandler}
            onCancelDelete={() => {
              setRowSelection({});
              setSelectedResultIds([]);
            }}
            moduleName={t("Results")}
          />

          {showAlert && (
            <AlertNotification
              showAlert={showAlert}
              message={alertMessage}
              alertType={messageType}
              onClose={onAlertClose}
            />
          )}
        </section>
      </div>
    );
  } else {
    return <AccessDeniedView />;
  }
};

export default ResultsListBody;
